import React, { useState, useEffect } from "react";
import { Grid, FormLabel, Select, MenuItem, FormControl } from "@material-ui/core";
import {
    AFFINITY_THRESHOLD_HELP_MESSAGE,
    HARDWARE_SOURCE_TO_AFFINITY_THRESHOLD_MAP
} from "./constant";
import { useStyles } from "src/styles/FormSelectStyles";
import HelpIconButton from "src/components/common/HelpIconButton";

interface Option {
    name: string,
    value: string
}

type HardwareSourceAndAffinityThresholdSelectsProps = {
    hardwareSource: string,
    affinityThreshold: string,
    hardwareSourceOptions: Array<Option>,
    showHardwareSourceError?: boolean,
    onSelectHardwareSource: (value:string) => void,
    onSelectAffinityThreshold: (value:string) => void
}

/**
 * The Component combine hardware source dropdown and affinity threshold dropdown.
 * Affinity threshold dropdown is disabled if user doesn't select a hardware source, and its default selection is
 * 0.7 for AFEX, 0.8 for VBI and ALL for ALL hardware sources.
 */
const HardwareSourceAndAffinityThresholdSelects: React.FunctionComponent<HardwareSourceAndAffinityThresholdSelectsProps> =
    (props: HardwareSourceAndAffinityThresholdSelectsProps) => {
        const styles = useStyles();
        const [selectedHardwareSource, setSelectedHardwareSource] = useState('');
        const [selectedAffinityThreshold, setSelectedAffinityThreshold] = useState('');

        useEffect(() => {
            setSelectedHardwareSource(props.hardwareSource);
            setSelectedAffinityThreshold(props.affinityThreshold)
        },[props.hardwareSource, props.affinityThreshold])

        let relatedAffinityThreshold: { name: string; value: string; }[] =[];
        if (selectedHardwareSource !== "") {
            const hardwareSourceTyped = selectedHardwareSource as keyof typeof HARDWARE_SOURCE_TO_AFFINITY_THRESHOLD_MAP;
            relatedAffinityThreshold = HARDWARE_SOURCE_TO_AFFINITY_THRESHOLD_MAP[hardwareSourceTyped];
        }
        const handleHardwareSourceChange = (event: React.ChangeEvent<any>) => {
            setSelectedHardwareSource(event.target.value);
            props.onSelectHardwareSource(event.target.value);
        }

        const handleAffinityThresholdChange = (event: React.ChangeEvent<any>) => {
            setSelectedAffinityThreshold(event.target.value);
            props.onSelectAffinityThreshold(event.target.value);
        }

        const listOfHardwareSource = props.hardwareSourceOptions.map((hardwareSourceOption) => (
            <MenuItem key={hardwareSourceOption.value} value={hardwareSourceOption.value}>
                {hardwareSourceOption.name}
            </MenuItem>
        ));

        const listOfAffinityThreshold = relatedAffinityThreshold.map((affinityThresholdOption) => (
            <MenuItem key={affinityThresholdOption.value} value={affinityThresholdOption.value}>
                {affinityThresholdOption.name}
            </MenuItem>
        ));

        return (
            <>
                <Grid item>
                    <FormControl fullWidth>
                        <Grid
                            container
                            alignItems="center"
                            spacing={1}>
                            <Grid item>
                                <FormLabel
                                    className={`${styles.selectLabel} ${props.showHardwareSourceError ? `${styles.errorLabel}` : ""}`}
                                    error={props.showHardwareSourceError}>
                                    Hardware Source:
                                </FormLabel>
                            </Grid>
                            <Grid item>
                                <Select
                                    className={styles.selectBox}
                                    disableUnderline
                                    displayEmpty
                                    value={selectedHardwareSource}
                                    onChange={handleHardwareSourceChange}>
                                    <MenuItem value="" disabled>Please select region first</MenuItem>
                                    {listOfHardwareSource}
                                </Select>
                            </Grid>
                        </Grid>
                    </FormControl>
                </Grid>
                <Grid item>
                    <FormControl fullWidth>
                        <Grid
                            container
                            alignItems="center"
                            spacing={1}>
                            <Grid item>
                                <FormLabel className={styles.selectLabel}>Affinity Threshold:</FormLabel>
                            </Grid>
                            <Grid item>
                                <Select
                                    className={styles.selectBox}
                                    disableUnderline
                                    displayEmpty
                                    value={selectedAffinityThreshold}
                                    onChange={handleAffinityThresholdChange}>
                                    <MenuItem value="ALL" disabled>Please select hardware source first</MenuItem>
                                    {listOfAffinityThreshold}
                                </Select>
                            </Grid>
                            <HelpIconButton message={AFFINITY_THRESHOLD_HELP_MESSAGE}/>
                        </Grid>
                    </FormControl>
                </Grid>
            </>
        );
    }

export default HardwareSourceAndAffinityThresholdSelects;